import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Minimal, dependency-free accessibility for a `role="dialog"` overlay.
 *
 * WIP-007 (docs/AUDIT-ACCESSIBILITY.md #4) found every dialog in this app —
 * GroupsDialog, ActivityEditorDialog, the run-sheet's dialogs, the Teach
 * roster/attendance dialogs — hand-rolling its own subset of modal behaviour:
 * some had Escape-to-close, none had a focus trap, and none moved focus into
 * the dialog on open or back to the trigger on close. A sighted mouse user
 * never notices; a keyboard user tabbing past the (invisible, behind an
 * overlay) rest of the page while a dialog is "open" does. There is no
 * shared Dialog component in `components/ui/` to fix once (CLAUDE.md: the
 * primitives are Button/Card/Spinner) — this hook is the equivalent for the
 * dialogs on this audit's priority surfaces, without inventing a new
 * component every call site would have to restructure around.
 *
 * On mount: remembers the currently-focused element, then focuses the first
 * focusable descendant of `containerRef` (or the container itself if none).
 * While active: Tab/Shift+Tab cycle within the container; Escape calls
 * `onClose`. On unmount: focus returns to whatever had it before the dialog
 * opened (typically the button that opened it).
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  active = true
): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = () => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    const first = focusables()[0];
    (first ?? container).focus({ preventScroll: true });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.({ preventScroll: true });
    };
    // Re-run only when `active` flips — re-focusing the dialog on every
    // parent re-render (e.g. while a save request is in flight) would steal
    // focus back from whatever the user just tabbed to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
